<script lang="ts">
  import { onDestroy, onMount, untrack, type Snippet } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { mdiPlus } from "@mdi/js"
  import { saveQuickRecipients } from "./save-quick-recipients"
  import {
    channelsForContactPointKind,
    normalizeEnabledChannelsForKind,
  } from "../../../../_logic/contact-channels"
  import QuickRecipientOverlayActions from "./_modules/quick-recipient-overlay-actions"
  import type {
    QuickRecipientContactRow,
    QuickRecipientSaveResult,
  } from "../../_shared/_logic/recipient-quick-edit-contract"
  import type { ContactOrderAppointmentRow } from "../../../../_logic/contact-orders-contract"
  import {
    buildQuickRecipientSavePlan,
    createBlankRecipientRow,
    hasQuickRecipientRowChanges,
    quickRecipientRowValidationError,
    quickRecipientRowsHaveChanges,
    quickRecipientRowsHaveValidationErrors,
    recipientRowsForContactOrder,
  } from "./_logic/contactRows"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId: string
    appointment: ContactOrderAppointmentRow
    workOrder: WorkOrder
    customers: Customer[]
    contactPoints: ContactPoint[]
    onSaved?: (result: QuickRecipientSaveResult) => void | Promise<void>
  }

  let {
    organizationId,
    appointment,
    workOrder,
    customers,
    contactPoints,
    onSaved = async () => {},
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  let rows = $state<QuickRecipientContactRow[]>(
    untrack(() => {
      const customersById = Object.fromEntries(customers.map((customer) => [customer.id, customer]))

      return recipientRowsForContactOrder({ row: appointment, customersById, contactPoints })
    }),
  )
  let submitting = $state(false)
  let validationMessage = $state<string | null>(null)

  const saveDisabled = $derived(
    submitting ||
      !quickRecipientRowsHaveChanges(rows) ||
      quickRecipientRowsHaveValidationErrors(rows),
  )

  const inputValue = (value: unknown) => {
    if (value instanceof Event) {
      const target = value.target as HTMLInputElement | HTMLTextAreaElement | null

      return `${target?.value ?? ""}`
    }

    return `${value ?? ""}`
  }

  type EditableContactPointKind = "EMAIL" | "MOBILE" | "FIXED_LINE"

  const enabledChannelValue = (kind: EditableContactPointKind, value: unknown) =>
    normalizeEnabledChannelsForKind(kind, Array.isArray(value) ? value : [])

  const channelLabels: Partial<Record<ContactChannel, string>> = {
    EMAIL: "E-Mail",
    SMS: "SMS",
    WHATSAPP: "WhatsApp",
    PHONE: "Telefon",
  }

  const channelOptions = (kind: EditableContactPointKind) =>
    channelsForContactPointKind(kind).map((channel) => ({
      name: channelLabels[channel] ?? channel,
      value: channel,
    }))

  const close = () => {
    if (submitting) return

    onremoveOverlay?.()
  }

  const addRow = () => {
    rows = [...rows, createBlankRecipientRow()]
    validationMessage = null
  }

  const updateRow = (clientId: string, patch: Partial<QuickRecipientContactRow>) => {
    rows = rows.map((row) => (row.clientId === clientId ? { ...row, ...patch } : row))
    validationMessage = null
  }

  const save = async () => {
    if (submitting) return

    const firstInvalid = rows
      .filter(hasQuickRecipientRowChanges)
      .map(quickRecipientRowValidationError)
      .find(Boolean)

    if (firstInvalid) {
      validationMessage = firstInvalid

      return
    }

    const plan = buildQuickRecipientSavePlan(rows)

    if (!plan.existingRows.length && !plan.newRows.length) {
      close()

      return
    }

    submitting = true
    try {
      const result = await saveQuickRecipients({
        organizationId,
        appointment,
        workOrder,
        contactPoints,
        plan,
      })
      await onSaved(result)
      newNotification({ class: "success", html: "Ansprechpartner gespeichert." })
      onremoveOverlay?.()
    } catch (err) {
      console.error("contact_order_recipient_quick_edit_save_failed", err)
      newNotification({ class: "error", html: "Ansprechpartner konnten nicht gespeichert werden." })
    } finally {
      submitting = false
    }
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="recipient-quick-overlay form">
  <p class="hint">
    Änderungen werden in den Kontaktdaten des Ansprechpartners gespeichert. Leeren Sie ein
    Kontaktfeld, wird dieser Kontaktweg beim Speichern gelöscht. Der Ansprechpartner bleibt
    erhalten; neue Ansprechpartner werden der passenden Liegenschaft oder Wohneinheit zugeordnet.
  </p>

  <div class="recipient-list">
    {#each rows as row (row.clientId)}
      <ActionBox>
        <div style="display: flex; flex-direction: column; gap: 1.2rem;">
          <div class="row">
            <Input
              type="text"
              label="Name"
              placeholder="Name"
              value={row.name}
              disabled={submitting}
              onChange={(value) => updateRow(row.clientId, { name: inputValue(value) })}
            />
          </div>
          <div class="row contact-point-row">
            <Input
              type="text"
              label="E-Mail"
              placeholder="name@example.de"
              value={row.email}
              disabled={submitting}
              onChange={(value) => updateRow(row.clientId, { email: inputValue(value) })}
            />
            <Input
              type="select"
              multiple={true}
              placeholder="Erlaubte Kanäle für E-Mail"
              value={row.emailEnabledChannels}
              disabled={submitting || !row.email.trim()}
              options={channelOptions("EMAIL")}
              onValueChange={(value) => {
                updateRow(row.clientId, {
                  emailEnabledChannels: enabledChannelValue("EMAIL", value),
                })
              }}
            />
          </div>
          <div class="row contact-point-row">
            <Input
              type="text"
              label="Mobil"
              placeholder="Mobilnummer"
              value={row.mobile}
              disabled={submitting}
              onChange={(value) => updateRow(row.clientId, { mobile: inputValue(value) })}
            />
            <Input
              type="select"
              multiple={true}
              placeholder="Erlaubte Kanäle für Mobil"
              value={row.mobileEnabledChannels}
              disabled={submitting || !row.mobile.trim()}
              options={channelOptions("MOBILE")}
              onValueChange={(value) => {
                updateRow(row.clientId, {
                  mobileEnabledChannels: enabledChannelValue("MOBILE", value),
                })
              }}
            />
          </div>
          <div class="row contact-point-row">
            <Input
              type="text"
              label="Festnetz"
              placeholder="Festnetznummer"
              value={row.fixedLine}
              disabled={submitting}
              onChange={(value) => updateRow(row.clientId, { fixedLine: inputValue(value) })}
            />
            <Input
              type="select"
              multiple={true}
              placeholder="Erlaubte Kanäle für Festnetz"
              value={row.fixedLineEnabledChannels}
              disabled={submitting || !row.fixedLine.trim()}
              options={channelOptions("FIXED_LINE")}
              onValueChange={(value) => {
                updateRow(row.clientId, {
                  fixedLineEnabledChannels: enabledChannelValue("FIXED_LINE", value),
                })
              }}
            />
          </div>
        </div>
      </ActionBox>
    {/each}
  </div>

  {#if validationMessage}
    <p class="validation-message">{validationMessage}</p>
  {/if}

  <CTA
    type="secondaryButton"
    content="Ansprechpartner hinzufügen"
    onClick={addRow}
    disabled={submitting}
  >
    {#snippet iconSnippet()}
      <Icon path={mdiPlus} />
    {/snippet}
  </CTA>
</div>

{#snippet actionButtons()}
  <QuickRecipientOverlayActions {submitting} {saveDisabled} onCancel={close} onSave={save} />
{/snippet}

<style lang="less">
  .recipient-quick-overlay {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .hint {
    margin: 0;
    color: color-mix(in srgb, var(--text-invers-100) 72%, transparent);
    line-height: 1.4;
  }

  .recipient-list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  .validation-message {
    margin: -0.2rem 0 0;
    color: var(--error-100, #b42318);
    font-weight: 700;
  }

  .contact-point-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem 1rem;
  }

  @media (max-width: 760px) {
    .recipient-quick-overlay {
      min-width: 0;
    }

    .contact-point-row {
      grid-template-columns: 1fr;
    }
  }
</style>
